import { useState } from "react";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Input,
  Show
} from "shared/components";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowUp01Icon,
  Cancel01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { cx } from "shared/utils";

type DateRangeProps = {
  label: string;
  fromValue: string;
  toValue: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
};

const getError = (
  type: "from" | "to",
  rawFrom: string,
  rawTo: string
): string | undefined => {
  if (type === "from")
    if (rawFrom && rawTo && rawFrom > rawTo)
      return "Start date is after end date";

  if (type === "to")
    if (rawFrom && rawTo && rawTo < rawFrom)
      return "End date is before start date";

  return undefined;
};

export const DateRange = ({
  label,
  fromValue,
  toValue,
  onFromChange,
  onToChange
}: DateRangeProps) => {
  const [open, setOpen] = useState(false);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFromChange("");
    onToChange("");
  };

  return (
    <div className="border-b border-alpha-40 pb-4">
      <button
        className="w-full"
        type="button"
        onClick={() => setOpen((prev) => !prev)}
      >
        <div className="flex w-full justify-between items-center">
          <p className="text-sm font-bold text-content-1000">{label}</p>
          <div className="flex items-center gap-2 h-[40px]">
            {fromValue || toValue ? (
              <IconButton
                HugeIcon={Cancel01Icon}
                variant={IconButtonVariant.BASE}
                size={IconButtonSize.SM}
                borderLess
                className={cx("!p-0.5 stroke-content-500 shrink-0")}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear(e);
                }}
              />
            ) : null}
            <HugeiconsIcon
              icon={ArrowUp01Icon}
              className={cx("transition-all", open ? "rotate-0" : "rotate-180")}
            />
          </div>
        </div>
      </button>
      <Show if={open}>
        <div className="mt-4">
          <div className="w-full flex gap-4">
            <div className="relative w-full">
              <Input
                id={`${label.toLowerCase().replace(/\s+/g, "-")}-from`}
                className={cx("w-full", !fromValue && "text-content-100")}
                type="date"
                value={fromValue}
                onChange={(e) => onFromChange(e.target.value)}
                error={getError("from", fromValue, toValue)}
              />
            </div>
            <div className="relative w-full">
              <Input
                id={`${label.toLowerCase().replace(/\s+/g, "-")}-to`}
                className={cx("w-full", !toValue && "text-content-100")}
                type="date"
                value={toValue}
                onChange={(e) => onToChange(e.target.value)}
                error={getError("to", fromValue, toValue)}
              />
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
};
